import React, { useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Grid, Table, TableBody, TableContainer, TableHead, TableRow } from '@mui/material';
import ScheduleTableCell from './ScheduleTableCell';
import {
  CalendarTableCell,
  ColoredCircle,
  PaymentPlanTableCell
} from './ScheduleTypeCellParamTemplateDetailsStyles';
import { Button, Typography } from '@totalsoft/rocket-ui';
import ScheduleTypeCellParamTemplateDetailsCalendarOptions from './ScheduleTypeCellParamTemplateDetailsCalendarOptions';
import { get, sequence, over } from '@totalsoft/change-tracking-react';
import { drop } from 'ramda';

const OfferPaymentPlanParamsCalendarView = ({ scheduleCellParamColumns, lens, isEditable, onResetDetailValues }) => {
  const { t } = useTranslation();
  const template = lens |> get;
  const months = Array.from(Array(12).keys());
  const dataColumns = useMemo(() => drop(2, scheduleCellParamColumns), [scheduleCellParamColumns]);

  const handleSelectAllMonths = useCallback(
    () => over(lens.scheduleTypeCellParamTemplateDetails, prev => prev.map(x => ({ ...x, selected: true }))),
    [lens.scheduleTypeCellParamTemplateDetails]
  );

  const handleDeselectAllMonths = useCallback(
    () => over(lens.scheduleTypeCellParamTemplateDetails, prev => prev.map(x => ({ ...x, selected: false }))),
    [lens.scheduleTypeCellParamTemplateDetails]
  );

  return (
    <>
      <Grid container mt={1} mb={2} gap={2} justifyContent='space-between'>
        <Grid item container xs={12} xl gap={1.5} order={{ xs: 2, xl: 1 }}>
          {dataColumns?.map((cellParam, index) => (
            <Grid key={cellParam.cellParamId} item container xs='auto' gap={0.5} alignItems='center'>
              <ColoredCircle colorIndex={index} />
              <Typography color='textSecondary' emphasis='bold'>
                {cellParam.cellParamDisplayName}
              </Typography>
            </Grid>
          ))}
        </Grid>
        <Grid item container xs={12} xl='auto' gap={1} alignItems='center' justifyContent='flex-end' order={{ xs: 1, xl: 2 }}>
          <Button color='primary' variant='outlined' onClick={onResetDetailValues} disabled={!isEditable}>
            {t('ScheduleTypeCellParamTemplate.Details.ClearOptions')}
          </Button>
          <Button color='primary' variant='outlined' onClick={handleSelectAllMonths} disabled={!isEditable}>
            {t('ScheduleTypeCellParamTemplate.Details.SelectAll')}
          </Button>
          <Button color='primary' variant='outlined' onClick={handleDeselectAllMonths} disabled={!isEditable}>
            {t('ScheduleTypeCellParamTemplate.Details.SelectNone')}
          </Button>
        </Grid>
      </Grid>
      <TableContainer>
        <Table size='small'>
          <TableHead>
            <TableRow>
              {months.map(month => (
                <PaymentPlanTableCell key={t(`Months.${[month]}`)} align='center'>
                  <Typography color='primary' emphasis='bold'>
                    {t(`Months.${[month]}`)}
                  </Typography>
                </PaymentPlanTableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow>
              {sequence(lens.scheduleTypeCellParamTemplateDetails)?.map((item, _idx) => {
                return (
                  <CalendarTableCell key={`monthNo_${item.monthNo |> get}`} align='center' sx={{ minWidth: '86px', maxWidth: '86px' }}>
                    <ScheduleTableCell
                      cellDataLens={item}
                      dataColumns={dataColumns}
                    />
                  </CalendarTableCell>
                );
              })}
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
      {template.scheduleTypeCellParamTemplateDetails && (
        <ScheduleTypeCellParamTemplateDetailsCalendarOptions dataColumns={dataColumns} lens={lens} isEditable={isEditable} />
      )}
    </>
  );
};

OfferPaymentPlanParamsCalendarView.propTypes = {
  lens: PropTypes.object.isRequired,
  scheduleCellParamColumns: PropTypes.array,
  isEditable: PropTypes.bool.isRequired,
  onResetDetailValues: PropTypes.func.isRequired
};

export default OfferPaymentPlanParamsCalendarView;
